/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { Button, Grid, Icon, List, Spacer } from "@cd2/components"
import { useTheme } from "@cd2/theme"
import { css, jsx } from "@emotion/core"
import React, { useRef, useState } from "react"
import { Link, NavLink } from "react-router-dom"
import Tippy from "@tippyjs/react"

import { useMediaBreakpoints } from "../hooks/useMediaBreakpoints"
import { DropdownMenu } from "./DropdownMenu"
import { useHelpRequestDialog } from "./HelpButton"
import { Logo } from "./Logo"
import { UserMenu } from "./UserMenu"
import { RefreshTokenBanner } from "./RefreshTokenBanner"
import { NotificationsBell } from "./Notifications"
import { CompanySwitcher, SidebarCompanySwitcher } from "./CompanySwitcher"
import { addDays, startOfDay } from "date-fns"
import { useAsyncCallback } from "../hooks/useAsyncCallback"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useGlobalState } from "../utils/globalState"
import { useSkeem } from "../providers/SkeemProvider"
import { Flex } from "./Flex"
import { icons } from "../utils/icons"
import { eRoutes } from "../routes"
import { transparencifyColor } from "@cd2/tools"
import { IconButton } from "./IconButton"
import { probationSidebarCountFilter, userCanAccessProbations } from "../utils/probationFlow"

const HEADER_HEIGHT = 75

interface IProps {
  disableMenu?: boolean
  children: React.ReactNode
  noPadding?: boolean
  noFooter?: boolean
  hasSidebar?: boolean
}

export function UserSiteWrapper({
  disableMenu,
  children,
  noPadding,
  noFooter,
  hasSidebar,
}: IProps) {
  if (hasSidebar) {
    return (
      <UserSiteWrapperWithSidebar
        children={children}
        disableMenu={disableMenu}
        noPadding={noPadding}
        noFooter={noFooter}
      />
    )
  } else {
    return (
      <UserSiteWrapperWithoutSidebar
        children={children}
        disableMenu={disableMenu}
        noPadding={noPadding}
        noFooter={noFooter}
      />
    )
  }
}

function UserSiteWrapperWithoutSidebar({ disableMenu, children, noPadding, noFooter }: IProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { isMobile, isTablet } = useMediaBreakpoints(ref)
  const isNarrow = isMobile || isTablet

  return (
    <div
      ref={ref}
      css={css`
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      `}
    >
      <RefreshTokenBanner />
      <Header disableMenu={disableMenu ?? false} isMobile={isMobile} isNarrow={isNarrow} />
      <div
        css={css`
          ${disableMenu
            ? css`
                pointer-events: none;
              `
            : ""}
        `}
      >
        <CompanySwitcher />
      </div>
      <div
        css={css`
          position: relative;
          flex: 1;
        `}
      >
        <div
          css={css`
            position: relative;
            padding: ${noPadding ? 0 : isNarrow ? 0 : 32}px 0;
            height: 100%;
          `}
        >
          {children}
        </div>
      </div>
      {!noFooter && (
        <div
          css={css`
            background-color: #f5f5f5;
          `}
        >
          <Spacer size={32} />
          <Links />
        </div>
      )}
    </div>
  )
}

function UserSiteWrapperWithSidebar({ disableMenu, children }: IProps) {
  const { isMobile, isTablet } = useMediaBreakpoints()
  const isNarrow = isMobile || isTablet

  return (
    <div
      css={css`
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      `}
    >
      <RefreshTokenBanner />
      <Header
        disableMenu={disableMenu ?? false}
        isMobile={isMobile}
        isNarrow={isNarrow}
        hasSidebar
      />
      <div
        css={css`
          flex: 1;
          display: grid;
          grid-template-columns: ${isMobile ? "100%" : "200px calc(100% - 200px)"};
        `}
      >
        {!isMobile && (
          <div
            css={css`
              position: sticky;
              top: 0;
              align-self: start;
              height: calc(100vh - ${HEADER_HEIGHT}px);
            `}
          >
            <Sidebar />
          </div>
        )}
        <div
          css={css`
            position: relative;
            flex: 1;
          `}
        >
          <div
            css={css`
              position: relative;
            `}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

function Header({
  disableMenu,
  isMobile,
  isNarrow,
  hasSidebar,
}: {
  disableMenu: boolean
  isMobile: boolean
  isNarrow: boolean
  hasSidebar?: boolean
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const globalState = useGlobalState()
  const theme = useTheme()
  const knowledgeBaseOnly =
    globalState.settings.useKnowledgeBase && globalState.settings.knowledgeBaseOnly

  const triggerHelpRequestDialog = useHelpRequestDialog()
  const handleHelpRequest = useAsyncCallback(async () => {
    await triggerHelpRequestDialog()
  })

  return (
    <div
      css={css`
        background-color: ${theme.themeColor("primary")};
        padding: ${isNarrow ? "4px 12px" : "12px"};
        box-shadow:
          0 1px 3px 0 rgba(0, 0, 0, 0.1),
          0 1px 2px 0 rgba(0, 0, 0, 0.06);
        position: relative;
        z-index: 85;
      `}
    >
      <Grid columns={["max-content", "auto", "max-content"]} alignItems="center">
        <div
          css={css`
            text-align: center;
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          {hasSidebar && isMobile && (
            <Tippy
              content={
                <div
                  css={css`
                    width: 200px;
                    max-height: calc(100vh - 100px);
                    overflow-y: auto;
                    background: white;
                    padding: 8px;
                    border-radius: 4px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
                  `}
                >
                  <SidebarContent onLinkClick={() => setSidebarOpen(false)} isInPopover />
                </div>
              }
              visible={sidebarOpen}
              onClickOutside={() => setSidebarOpen(false)}
              placement="bottom-start"
              interactive={true}
              arrow={false}
              offset={[0, 0]}
            >
              <div>
                <IconButton
                  icon={icons.hamburgerMenu}
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  color="white"
                  iconSize={20}
                />
              </div>
            </Tippy>
          )}
          <Link to="/">
            {isNarrow ? (
              <div
                css={css`
                  width: 36px;
                  display: inline-block;

                  svg {
                    display: block;
                  }
                `}
              >
                <Logo height={50} iconOnLeft logoOnly />
              </div>
            ) : (
              <Logo height={50} iconOnLeft />
            )}
          </Link>
        </div>
        <div />
        <div
          css={css`
            text-align: right;
            ${disableMenu
              ? css`
                  pointer-events: none;
                `
              : ""}
          `}
        >
          <Flex alignItems="center" justifyContent="end">
            <DropdownMenu
              disabled={disableMenu}
              target={() => (
                <Button
                  buttonColor="#fff"
                  buttonStyle="text"
                  hoverColor="rgba(0,0,0,0.1)"
                  extendTheme={{
                    color: "white",
                  }}
                >
                  Help
                  {!isMobile && (
                    <div
                      css={css`
                        display: inline-block;
                        vertical-align: middle;
                        margin-left: 8px;
                      `}
                    >
                      <Icon icon={icons.helpRequests} color="white" size={20} />
                    </div>
                  )}
                </Button>
              )}
              items={[
                {
                  children: "Updates",
                  to: "/updates",
                },
                !knowledgeBaseOnly && {
                  children: "Help videos",
                  to: eRoutes["/help-videos"],
                },
                {
                  children: "Ask for help",
                  onClick: handleHelpRequest,
                },
                {
                  children: "Your help requests",
                  to: "/help-requests",
                },
              ].filter(Boolean)}
            />
            <NotificationsBell />
            <UserMenu disabled={disableMenu} />
          </Flex>
        </div>
      </Grid>
    </div>
  )
}

function Links() {
  const breakpoints = useMediaBreakpoints()
  return (
    <div
      css={css`
        text-align: center;

        a {
          border-bottom: 1px dashed;
        }
      `}
    >
      <List justifyContent="center" horizontal>
        <Link
          to="/terms-and-conditions"
          css={css`
            color: #666;
            text-decoration: none;
          `}
        >
          Individual user terms
        </Link>
        <Link
          to="/eula"
          css={css`
            color: #666;
            text-decoration: none;
          `}
        >
          Acceptable use policy
        </Link>
      </List>
      <Spacer size={breakpoints.isMobile ? 80 : 32} />
    </div>
  )
}

function Sidebar() {
  return (
    <Flex
      direction="column"
      css={css`
        height: 100%;
        width: 200px;
        padding: 8px;
      `}
    >
      <Spacer size={16} />
      <SidebarContent />
    </Flex>
  )
}

function SidebarContent({
  onLinkClick,
  isInPopover,
}: {
  onLinkClick?: () => void
  isInPopover?: boolean
} = {}) {
  const globalState = useGlobalState()

  return (
    <>
      <SidebarCompanySwitcher />
      <Spacer size={16} />
      <Grid
        gap={8}
        css={css`
          overflow: auto;
        `}
      >
        <SidebarLink
          to={eRoutes["/"]}
          icon={icons.dashboard}
          label="Home"
          exact={true}
          onClick={onLinkClick}
        />
        <SidebarLink
          to={eRoutes["/results"]}
          icon={icons.results}
          label="Cases"
          onClick={onLinkClick}
        />
        {globalState.user.isManager && (
          <SidebarLink
            to={eRoutes["/flow-comments"]}
            icon={icons.comments}
            label="My Feedback"
            onClick={onLinkClick}
          />
        )}
        <SidebarLink
          to={eRoutes["/files"]}
          icon={icons.files}
          label="Documents"
          onClick={onLinkClick}
        />
        {globalState.user.managedTeams.length > 0 && (
          <SidebarLink
            to={eRoutes["/teams"]}
            icon={icons.teams}
            label="Teams"
            onClick={onLinkClick}
          />
        )}
        {globalState.settings.useRota && (
          <SidebarLink to="/rota" icon={icons.rota} label="Team View" onClick={onLinkClick} />
        )}
        {globalState.permissions.canManageCompany && (
          <SidebarLink
            to={eRoutes["/manager/users"]}
            icon={icons.users}
            label="Users"
            onClick={onLinkClick}
          />
        )}
        {(globalState.permissions.canManageCompany || globalState.permissions.canManageEmployees) &&
          !globalState.settings.disableEmployeeFeatures && (
            <SidebarLink
              to={eRoutes["/manager/employees"]}
              icon={icons.employees}
              label="Employees"
              onClick={onLinkClick}
            />
          )}
        {globalState.settings.enableProbations && !globalState.settings.disableEmployeeFeatures && (
          <ProbationSidebarLink onClick={onLinkClick} />
        )}
        {globalState.permissions.canManageCompany && (
          <SidebarLink
            label="Company settings"
            to="/manage-team"
            icon={icons.settings}
            onClick={onLinkClick}
          />
        )}
      </Grid>
      {!isInPopover && (
        <>
          <Flex.Spacer />
          <div
            css={css`
              height: 120px;
            `}
          />
          <div
            css={css`
              padding: 8px 12px;
              position: fixed;
              bottom: 8px;
            `}
          >
            <div
              css={css`
                line-height: 1.5;
              `}
            >
              &copy; CleverGoose {new Date().getFullYear()}.
              <br />
              All rights reserved.
            </div>
            <Spacer size={4} />
            <div
              css={css`
                color: #737585;
                a {
                  display: block;
                  padding: 4px;
                  color: #737585;
                  text-decoration: none;
                }
              `}
            >
              <Link to="/privacy-policy">Individual user terms</Link>
              <Link to="/eula">Acceptable use policy</Link>
            </div>
          </div>
        </>
      )}
    </>
  )
}

function ProbationSidebarLink({ onClick }: { onClick?: () => void }) {
  const globalState = useGlobalState()
  const skeem = useSkeem()
  const [hasAccess, setHasAccess] = useState<boolean | null>(
    globalState.permissions.canManageEmployees ? true : null,
  )
  const [count, setCount] = useState<number | null>(null)

  useAsyncEffect(
    async ({ isAlive }) => {
      const scopeContext = {
        permissions: globalState.permissions,
        accessibleCompanyIds: globalState.accessibleCompanyIds,
      }

      if (!globalState.permissions.canManageEmployees) {
        const allowed = await userCanAccessProbations(scopeContext)
        if (!isAlive()) return
        if (!allowed) {
          setHasAccess(false)
          return
        }
        setHasAccess(true)
      }

      const today = startOfDay(new Date())
      const thirtyDaysFromNow = addDays(today, 30)

      const result = await skeem.fetch2<number>({
        count: {
          name: "employees",
          filter: probationSidebarCountFilter(scopeContext, thirtyDaysFromNow),
        },
      })
      if (isAlive()) setCount(result)
    },
    [globalState.accessibleCompanyIds, globalState.permissions.canManageEmployees],
  )

  if (hasAccess !== true) return null

  return (
    <SidebarLink
      to={eRoutes["/probations"]}
      icon={icons.probation}
      label="Probation"
      count={count ?? undefined}
      onClick={onClick}
    />
  )
}

function SidebarLink({
  to,
  icon,
  label,
  exact,
  count,
  onClick,
}: {
  to: string
  icon: string
  label: string
  exact?: boolean
  count?: number
  onClick?: () => void
}) {
  const theme = useTheme()

  return (
    <NavLink
      to={to}
      onClick={onClick}
      css={css`
        display: grid;
        grid-template-columns: 32px auto;
        grid-gap: 8px;
        align-items: center;
        padding: 8px;
        text-decoration: none;
        transition: 0.2s;
        color: #737585;
        border-radius: 8px;
        font-size: 14px;

        &:hover {
          background-color: ${transparencifyColor(theme.themeColor("primaryLight"), 0.1)};
        }
        &.active {
          color: ${theme.themeColor("primary")};
        }
      `}
      exact={exact}
    >
      <div
        css={css`
          justify-self: center;
        `}
      >
        <Icon icon={icon} size={20} />
      </div>
      <span
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          min-width: 0;
        `}
      >
        <span>{label}</span>
        {count !== undefined && count > 0 && (
          <span
            css={css`
              background: ${theme.themeColor("primary")};
              color: white;
              font-size: 12px;
              font-weight: 600;
              padding: 2px 6px;
              border-radius: 10px;
              flex-shrink: 0;
            `}
          >
            {count}
          </span>
        )}
      </span>
    </NavLink>
  )
}
